import React from 'react';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmployeeImageProps {
  name: string;
  photoUrl?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'h-10 w-10 text-xs',
  md: 'h-12 w-12 text-sm',
  lg: 'h-16 w-16 text-base',
  xl: 'h-20 w-20 text-lg',
};

/**
 * EmployeeImage component displays a rectangular image with the employee's photo
 * or initials as a fallback. Optimized for the Approvals screen.
 */
function EmployeeImageComponent({ 
  name, 
  photoUrl, 
  size = 'md',
  className 
}: EmployeeImageProps) {
  // Track which URL loaded/failed rather than a plain boolean, so keeping the
  // same photoUrl across renders never replays the fade-in.
  const [loadedUrl, setLoadedUrl] = React.useState<string | undefined>(undefined);
  const [erroredUrl, setErroredUrl] = React.useState<string | undefined>(undefined);

  const imageLoaded = !!photoUrl && loadedUrl === photoUrl;
  const imageError = !!photoUrl && erroredUrl === photoUrl;

  // Generate initials from name (first letter of first and last name)
  const getInitials = (fullName: string): string => {
    const parts = fullName.trim().split(' ');
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(name);
  const showFallback = !photoUrl || imageError;

  return (
    <div 
      className={cn(
        'relative flex shrink-0 overflow-hidden rounded-md border border-border bg-muted',
        sizeClasses[size],
        className
      )}
    >
      {photoUrl && !imageError && (
        <img 
          src={photoUrl} 
          alt={`${name}'s profile photo`}
          className={cn(
            "h-full w-full object-cover transition-opacity duration-200",
            imageLoaded ? "opacity-100" : "opacity-0"
          )}
          loading="lazy"
          onLoad={() => setLoadedUrl(photoUrl)}
          onError={() => {
            console.warn(`Failed to load image for ${name}: ${photoUrl}`);
            setErroredUrl(photoUrl);
          }}
        />
      )}
      {showFallback && (
        <div 
          className="flex h-full w-full items-center justify-center bg-primary/10 text-primary font-semibold"
          aria-label={`${name}'s avatar`}
        >
          {initials || <User className="h-1/2 w-1/2" />}
        </div>
      )}
    </div>
  );
}

export const EmployeeImage = React.memo(EmployeeImageComponent);
